import '../styles/app.css';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { useSearch } from '../lib/hooks.js';
import { Button } from '../ui/index.js';

const NAV: { to: string; icon: string; label: string; end?: boolean }[] = [
  { to: '/', icon: '▚', label: 'Dashboard', end: true },
  { to: '/leads', icon: '✦', label: 'Leads' },
  { to: '/customers', icon: '☺', label: 'Customers' },
  { to: '/jobs', icon: '✍', label: 'Jobs' },
  { to: '/schedule', icon: '◷', label: 'Schedule' },
  { to: '/dispatch', icon: '⇄', label: 'Dispatch' },
  { to: '/inbox', icon: '☏', label: 'Inbox' },
  { to: '/invoices', icon: '＄', label: 'Invoices' },
  { to: '/automations', icon: '⚡', label: 'Automations' },
  { to: '/website', icon: '◆', label: 'Website' },
];

function GlobalSearch() {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const { data } = useSearch(term);
  const navigate = useNavigate();
  const results = [
    ...(data?.byName ?? []).map((c) => ({ id: c.id, label: c.displayName, sub: 'customer' })),
    ...(data?.byPhone ?? []).map((c) => ({ id: c.cid, label: c.displayName, sub: c.value })),
    ...(data?.byEmail ?? []).map((c) => ({ id: c.cid, label: c.displayName, sub: c.value })),
  ].filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i);

  return (
    <div className="searchbox" onBlur={() => setTimeout(() => setOpen(false), 150)}>
      <span className="search-ico" aria-hidden>⌕</span>
      <input
        className="input" placeholder="Search customers by name, phone, email…" aria-label="Search"
        value={term} onFocus={() => setOpen(true)} onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
      />
      {open && term.trim().length >= 2 && (
        <div className="search-menu">
          {results.length === 0 ? (
            <div className="search-item muted">No matches</div>
          ) : results.map((r) => (
            <div key={r.id} className="search-item" onMouseDown={() => { navigate(`/customers/${r.id}`); setTerm(''); setOpen(false); }}>
              <strong>{r.label}</strong> <span className="subtle">· {r.sub}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const go = (path: string) => { setOpen(false); navigate(path); };
  return (
    <div className="newmenu" onBlur={() => setTimeout(() => setOpen(false), 150)}>
      <Button size="sm" onClick={() => setOpen((v) => !v)}>+ New</Button>
      {open && (
        <div className="newmenu-list">
          <div className="newmenu-item" onMouseDown={() => go('/leads?new=1')}><span className="nav-ico">✦</span> New lead</div>
          <div className="newmenu-item" onMouseDown={() => go('/customers?new=1')}><span className="nav-ico">☺</span> New customer</div>
          <div className="newmenu-item" onMouseDown={() => go('/customers')}><span className="nav-ico">✎</span> New proposal…</div>
          <div className="newmenu-item" onMouseDown={() => go('/customers')}><span className="nav-ico">✍</span> New job…</div>
          <div className="newmenu-item" onMouseDown={() => go('/customers')}><span className="nav-ico">＄</span> New invoice…</div>
          <div className="newmenu-item subtle" style={{ fontSize: 'var(--text-xs)', cursor: 'default' }}>Proposals, jobs & invoices start from a customer</div>
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { session, signOut } = useAuth();
  const [theme, setTheme] = useState<string>(() => document.documentElement.getAttribute('data-theme') || 'light');
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme || 'light');
    localStorage.setItem('worksy360.theme', theme || 'light');
  }, [theme]);

  const initials = (session?.userName ?? 'W').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="shell">
      <aside className={`sidebar ${navOpen ? 'open' : ''}`}>
        <div className="brand"><img src="/logo_favicon.png" alt="" className="brand-logo" /> Worksy360</div>
        <nav className="nav" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={() => setNavOpen(false)}>
              <span className="nav-ico" aria-hidden>{n.icon}</span>{n.label}
            </NavLink>
          ))}
          <div className="nav-section">System</div>
          <NavLink to="/tax" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={() => setNavOpen(false)}><span className="nav-ico" aria-hidden>▤</span>Tax Center</NavLink>
          <NavLink to="/settings" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={() => setNavOpen(false)}><span className="nav-ico" aria-hidden>⚙</span>Settings</NavLink>
        </nav>
        <div className="sidebar-foot row between">
          <div className="row gap-2">
            <span className="avatar">{initials}</span>
            <div className="stack"><span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{session?.userName}</span><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{session?.roles.join(', ')}</span></div>
          </div>
          <Button variant="ghost" size="sm" onClick={signOut} title="Sign out">⎋</Button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <Button variant="ghost" size="sm" onClick={() => setNavOpen((v) => !v)} className="only-mobile" aria-label="Menu" style={{ display: 'none' }}>☰</Button>
          <GlobalSearch />
          <div className="grow" />
          <NewMenu />
          <Button variant="ghost" size="sm" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} title="Toggle theme">
            {theme === 'dark' ? '☀' : '☾'}
          </Button>
        </header>
        <main className="content"><Outlet /></main>
      </div>
    </div>
  );
}
